<template>
    <transition name="piv-fade">
        <div v-if="visible && images.length" class="piv" role="dialog" aria-modal="true" aria-label="Visualização das imagens do produto"
            @click.self="close()">
            <button type="button" class="piv-close" aria-label="Fechar" @click="close()">
                <fa icon="times" />
            </button>

            <div class="piv-stage" @click.self="close()"
                @touchstart.passive="onTouchStart" @touchend.passive="onTouchEnd">
                <button v-if="hasMany" type="button" class="piv-arrow is-prev" aria-label="Imagem anterior" @click="go(-1)">
                    <fa icon="chevron-left" />
                </button>

                <transition :name="`piv-slide-${direction}`" mode="out-in">
                    <figure :key="index" class="piv-figure" :class="{'is-inactive': !current.isActive}">
                        <img :src="current.url" :alt="`Imagem ${index + 1} de ${images.length}`" />
                    </figure>
                </transition>

                <button v-if="hasMany" type="button" class="piv-arrow is-next" aria-label="Próxima imagem" @click="go(1)">
                    <fa icon="chevron-right" />
                </button>
            </div>

            <footer class="piv-info">
                <div class="piv-meta">
                    <span class="piv-counter">
                        <strong>{{index + 1}}</strong><span>/ {{images.length}}</span>
                    </span>

                    <span class="piv-badge" :class="current.isActive ? 'is-active' : 'is-inactive'">
                        <span class="piv-dot"></span>
                        {{current.isActive ? 'Ativa' : 'Inativa'}}
                    </span>

                    <span v-if="current.isMain" class="piv-badge is-main" :title="current.isActive ? '' : 'Marcada como principal, mas inativa'">
                        <fa icon="crown" />
                        Principal
                    </span>

                    <span v-if="current.getSizeInformation && current.getSizeInformation()" class="piv-size">
                        {{current.getSizeInformation()}}
                    </span>
                </div>

                <div v-if="hasMany" class="piv-strip" ref="strip">
                    <button v-for="(img, i) in images" :key="i" type="button" class="piv-thumb"
                        :class="{'is-current': i === index, 'is-inactive': !img.isActive, 'is-main': img.isMain && img.isActive}"
                        :aria-label="`Ver imagem ${i + 1}`"
                        :aria-current="i === index ? 'true' : null"
                        @click="goTo(i)">
                        <img :src="img.url" alt="" />
                    </button>
                </div>
            </footer>
        </div>
    </transition>
</template>

<script>
// Lightbox das imagens do produto: navega entre as imagens da lista recebida
// e mostra se cada uma está ativa e se é a principal.
export default {
    name: 'ProductImageViewerCmp',
    props: {
        images: {
            type: Array,
            default: () => [],
        },
        startIndex: {
            type: Number,
            default: 0,
        },
        visible: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            index: 0,
            direction: 'next',
            touchX: null,
        }
    },
    computed: {
        hasMany() {
            return this.images.length > 1
        },
        current() {
            return this.images[this.index] || {}
        },
    },
    watch: {
        visible: {
            immediate: true,
            handler(isVisible) {
                if (isVisible) {
                    this.index = this.clamp(this.startIndex)
                    this.direction = 'next'
                    window.addEventListener('keydown', this.onKeydown)
                    document.body.style.overflow = 'hidden'
                    this.$nextTick(this.scrollThumbIntoView)
                } else {
                    this.teardown()
                }
            },
        },
        index() {
            this.$nextTick(this.scrollThumbIntoView)
        },
    },
    beforeDestroy() {
        this.teardown()
    },
    methods: {
        clamp(i) {
            if (!this.images.length) {
                return 0
            }

            return Math.min(Math.max(i || 0, 0), this.images.length - 1)
        },
        go(step) {
            if (!this.hasMany) {
                return
            }

            this.direction = step > 0 ? 'next' : 'prev'
            this.index = (this.index + step + this.images.length) % this.images.length
        },
        goTo(i) {
            if (i === this.index) {
                return
            }

            this.direction = i > this.index ? 'next' : 'prev'
            this.index = i
        },
        close() {
            this.$emit('close')
        },
        teardown() {
            window.removeEventListener('keydown', this.onKeydown)
            document.body.style.overflow = ''
        },
        onKeydown(e) {
            if (e.key === 'Escape') {
                this.close()
            } else if (e.key === 'ArrowLeft') {
                this.go(-1)
            } else if (e.key === 'ArrowRight') {
                this.go(1)
            }
        },
        onTouchStart(e) {
            this.touchX = e.changedTouches[0].clientX
        },
        onTouchEnd(e) {
            if (this.touchX === null) {
                return
            }

            const delta = e.changedTouches[0].clientX - this.touchX
            this.touchX = null

            if (Math.abs(delta) > 40) {
                this.go(delta < 0 ? 1 : -1)
            }
        },
        scrollThumbIntoView() {
            const strip = this.$refs.strip

            if (!strip) {
                return
            }

            const thumb = strip.children[this.index]

            if (thumb) {
                strip.scrollTo({
                    left: thumb.offsetLeft - (strip.clientWidth - thumb.clientWidth) / 2,
                    behavior: 'smooth',
                })
            }
        },
    },
}
</script>

<style lang="scss" scoped>
$display: 'Bricolage Grotesque', 'Source Sans Pro', system-ui, sans-serif;
$mono: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
$ease-out: cubic-bezier(.22, 1, .36, 1);

// O palco é sempre escuro, nos dois temas (tokens de tooltip): a imagem é a protagonista
.piv {
    position: fixed;
    inset: 0;
    z-index: 1050;
    display: grid;
    grid-template-rows: minmax(0, 1fr) auto;
    padding: 56px 16px 18px;
    color: var(--text-on-tooltip);
    background:
        radial-gradient(ellipse at 50% 38%, color-mix(in srgb, var(--surface-tooltip) 70%, transparent), transparent 70%),
        color-mix(in srgb, var(--surface-tooltip-deep) 92%, transparent);
    backdrop-filter: blur(6px);

    @media (min-width: 768px) {
        padding: 56px 32px 22px;
    }
}

.piv-close {
    position: absolute;
    top: 12px;
    right: 14px;
    display: grid;
    place-items: center;
    width: 40px;
    height: 40px;
    border: 1px solid color-mix(in srgb, var(--text-on-tooltip) 14%, transparent);
    border-radius: 50%;
    color: inherit;
    background: color-mix(in srgb, var(--text-on-tooltip) 6%, transparent);
    font-size: 17px;
    transition: background .2s, transform .2s $ease-out;

    &:hover {
        background: color-mix(in srgb, var(--text-on-tooltip) 14%, transparent);
        transform: rotate(90deg);
    }
}

.piv-stage {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 0;
}

.piv-figure {
    display: flex;
    align-items: center;
    justify-content: center;
    max-width: 100%;
    height: 100%;
    margin: 0;

    img {
        max-width: 100%;
        max-height: 100%;
        object-fit: contain;
        border-radius: 6px;
        box-shadow: 0 30px 80px -20px color-mix(in srgb, var(--shadow-ink) 70%, transparent);
        user-select: none;
    }

    &.is-inactive img {
        filter: grayscale(.85);
        opacity: .55;
    }
}

.piv-arrow {
    position: absolute;
    top: 50%;
    z-index: 1;
    display: grid;
    place-items: center;
    width: 48px;
    height: 48px;
    margin-top: -24px;
    border: 1px solid color-mix(in srgb, var(--text-on-tooltip) 16%, transparent);
    border-radius: 50%;
    color: inherit;
    background: color-mix(in srgb, var(--surface-tooltip-deep) 60%, transparent);
    font-size: 17px;
    transition: background .2s, transform .2s $ease-out;

    &.is-prev {
        left: 0;
        &:hover { transform: translateX(-3px); }
    }

    &.is-next {
        right: 0;
        &:hover { transform: translateX(3px); }
    }

    &:hover {
        background: color-mix(in srgb, var(--text-on-tooltip) 16%, transparent);
    }

    @media (min-width: 768px) {
        width: 56px;
        height: 56px;
        margin-top: -28px;

        &.is-prev { left: 8px; }
        &.is-next { right: 8px; }
    }
}

.piv-info {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 14px;
    padding-top: 16px;
}

.piv-meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    gap: 8px 10px;
}

.piv-counter {
    display: inline-flex;
    align-items: baseline;
    gap: 5px;
    margin-right: 6px;
    font-family: $display;

    strong {
        font-size: 22px;
        font-weight: 700;
        font-variant-numeric: tabular-nums;
    }

    span {
        color: var(--text-on-tooltip-muted);
        font-size: 14px;
        font-variant-numeric: tabular-nums;
    }
}

.piv-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 3px 11px;
    border: 1px solid;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: .06em;
    text-transform: uppercase;

    &.is-active {
        color: var(--success-on-dark);
        border-color: color-mix(in srgb, var(--success-on-dark) 35%, transparent);
        background: color-mix(in srgb, var(--success-on-dark) 10%, transparent);
    }

    &.is-inactive {
        color: var(--warning-solid);
        border-color: color-mix(in srgb, var(--warning-solid) 35%, transparent);
        background: color-mix(in srgb, var(--warning-solid) 12%, transparent);
    }

    &.is-main {
        color: var(--text-on-tooltip);
        border-color: color-mix(in srgb, var(--text-on-tooltip) 25%, transparent);
        background: color-mix(in srgb, var(--text-on-tooltip) 8%, transparent);

        svg {
            color: var(--warning-solid);
            font-size: 11px;
        }
    }
}

.piv-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: currentColor;
}

.piv-size {
    color: var(--text-on-tooltip-muted);
    font-family: $mono;
    font-size: 12px;
}

.piv-strip {
    display: flex;
    gap: 8px;
    max-width: 100%;
    padding: 4px 2px 6px;
    overflow-x: auto;
    scrollbar-width: thin;
    scrollbar-color: color-mix(in srgb, var(--text-on-tooltip) 25%, transparent) transparent;
}

.piv-thumb {
    position: relative;
    flex-shrink: 0;
    width: 56px;
    height: 56px;
    padding: 0;
    overflow: hidden;
    border: 2px solid transparent;
    border-radius: 10px;
    background: color-mix(in srgb, var(--text-on-tooltip) 6%, transparent);
    opacity: .55;
    transition: opacity .2s, border-color .2s, transform .2s $ease-out;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }

    &:hover {
        opacity: .9;
    }

    &.is-current {
        border-color: var(--text-on-tooltip);
        opacity: 1;
        transform: translateY(-2px);
    }

    &.is-inactive img {
        filter: grayscale(1);
    }

    &.is-main::after {
        content: '';
        position: absolute;
        top: 4px;
        right: 4px;
        width: 7px;
        height: 7px;
        border-radius: 50%;
        background: var(--warning-solid);
        box-shadow: 0 0 0 2px color-mix(in srgb, var(--surface-tooltip-deep) 70%, transparent);
    }
}

// Abertura
.piv-fade-enter-active,
.piv-fade-leave-active {
    transition: opacity .22s ease;

    .piv-figure {
        transition: transform .3s $ease-out;
    }
}

.piv-fade-enter,
.piv-fade-leave-to {
    opacity: 0;

    .piv-figure {
        transform: scale(.96);
    }
}

// Troca de imagem, na direção da navegação
.piv-slide-next-enter-active,
.piv-slide-next-leave-active,
.piv-slide-prev-enter-active,
.piv-slide-prev-leave-active {
    transition: opacity .18s ease, transform .22s $ease-out;
}

.piv-slide-next-enter,
.piv-slide-prev-leave-to {
    opacity: 0;
    transform: translateX(28px);
}

.piv-slide-next-leave-to,
.piv-slide-prev-enter {
    opacity: 0;
    transform: translateX(-28px);
}

@media (prefers-reduced-motion: reduce) {
    .piv-fade-enter-active,
    .piv-fade-leave-active,
    .piv-slide-next-enter-active,
    .piv-slide-next-leave-active,
    .piv-slide-prev-enter-active,
    .piv-slide-prev-leave-active {
        transition: none;
    }
}
</style>
